import clsx from 'clsx'
import { format } from 'date-fns'
import * as React from 'react'
import { useState } from 'react'
import { ReactDatePickerProps } from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import Calendar from '@/components-latest/primitives/Calendar/Calendar'
import { inputVariants } from '@/components-latest/primitives/Controls/Input/Input'
import { Calendar as CalendarIcon } from '@/components-latest/primitives/Icons'
import Popover, {
  PopoverContent,
  PopoverTrigger
} from '@/components-latest/primitives/Popover/Popover'
import { textVariants } from '@/components-latest/primitives/Text'

import Styles from './DatePicker.module.scss'

interface IDatepickerProps extends Omit<ReactDatePickerProps, 'value'> {
  onChange: (date: Date | null) => void
  value: Date | null | undefined
  className?: string
  disabled?: boolean
  placeholder?: string
}

const DatePicker = React.forwardRef<HTMLButtonElement, IDatepickerProps>(
  (
    { className, value, disabled, placeholder, onChange, ...restProps },
    ref
  ) => {
    const { t } = useTranslation()

    const [open, setOpen] = useState(false)

    if (disabled) {
      value = null
    }

    const onChangeEnhanced = (date: Date | null) => {
      onChange(date)
      setOpen(false)
    }

    return (
      <Popover open={open} onOpenChange={(isOpen: boolean) => setOpen(isOpen)}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            className={clsx(
              Styles.triggerText,
              inputVariants({ intent: 'default' }),
              textVariants({ variant: 'nextSubtitle' }),
              className
            )}
            disabled={disabled}>
            <CalendarIcon />
            {value ? (
              format(value, 'MMM dd, yyyy')
            ) : (
              <span data-placeholder={true}>
                {placeholder || t('common.date.choose-date')}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className={Styles.content}>
          <Calendar
            date={value ?? null}
            onChange={onChangeEnhanced}
            {...restProps}
          />
          <div className={Styles.actionRow}>
            <Button
              variant="secondary"
              onClick={() => onChangeEnhanced(new Date())}>
              {t('common.date.today')}
            </Button>
            <Button variant="tertiary" onClick={() => onChangeEnhanced(null)}>
              {t('common.cta.clear')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)
DatePicker.displayName = 'DatePicker'

export type { IDatepickerProps }
export default DatePicker
